/**
 * Complex content operations for multi-line paste and text splitting
 * 
 * This module provides utilities for:
 * - Handling multi-line paste with content distribution
 * - Splitting content at cursor position
 * - Moving content between paragraph rows
 */

import {sanitizeHTML, SELECTORS} from './constants';
import {getStructureIndex, getParamIndex, isParagraphRow, isVariableSpan} from './elementHelpers';
import {getTextContent, endsWithWhitespace, startsWithWhitespace} from './textHelpers';
import {getCursorOffsetInElement, getSelectionEndOffsetInElement} from './rangeHelpers';
import {focusElementAtOffset} from './navigationHelpers';
import {EMPTY_PARAGRAPH} from '../../hooks/useParagraphEditor';

function isEmptyContent(content: string): boolean {
    const text = getTextContent(content);
    return !text.trim() || text.trim() === EMPTY_PARAGRAPH;
}

function findParagraphRows(
    referenceIndex: number,
    predicate: (index: number, referenceIndex: number) => boolean,
    returnFirst: boolean = false
): HTMLElement[] {
    const allEditables = document.querySelectorAll(SELECTORS.STRUCTURE_INDEX);
    const matches: HTMLElement[] = [];
    
    for(let i = 0; i < allEditables.length; i++) {
        const el = allEditables[i] as HTMLElement;
        const idx = getStructureIndex(el);
        
        if(isParagraphRow(el) && predicate(idx, referenceIndex)) {
            if(returnFirst) return [el];
            matches.push(el);
        }
    }
    return matches;
}

function cloneNodeToContainer(
    node: Node,
    currentOffset: number,
    cursorOffset: number,
    offsetIncrement: number,
    beforeContainer: HTMLElement,
    afterContainer: HTMLElement
): {newOffset: number; splitDone: boolean} {
    if(currentOffset >= cursorOffset) {
        afterContainer.appendChild(node.cloneNode(true));
        return {newOffset: currentOffset, splitDone: true};
    } else {
        beforeContainer.appendChild(node.cloneNode(true));
        return {newOffset: currentOffset + offsetIncrement, splitDone: false};
    }
}

function updateRowAndFocus(
    row: HTMLElement,
    content: string,
    focusPosition: number
): void {
    updateElementContent(row, content);
    focusElementAtOffset(row, focusPosition);
}

function getEditableElementsAfter(currentElement: HTMLElement): HTMLElement[] {
    const currentStructureIndex = getStructureIndex(currentElement);
    const currentParamIndex = getParamIndex(currentElement) ?? -1;
    const allEditables = document.querySelectorAll(SELECTORS.STRUCTURE_INDEX);
    const results: HTMLElement[] = [];

    for(let i = 0; i < allEditables.length; i++) {
        const el = allEditables[i] as HTMLElement;
        const si = getStructureIndex(el);
        const pi = getParamIndex(el) ?? -1;

        if(si === currentStructureIndex && pi > currentParamIndex) {
            results.push(el);
        } else if(si > currentStructureIndex) {
            results.push(el);
        }
    }

    return results.sort((a, b) => {
        const siA = getStructureIndex(a);
        const siB = getStructureIndex(b);
        if(siA !== siB) return siA - siB;
        return (getParamIndex(a) ?? -1) - (getParamIndex(b) ?? -1);
    });
}

function getParagraphRowsAfter(startIndex: number): HTMLElement[] {
    return findParagraphRows(
        startIndex,
        (idx, ref) => idx > ref,
        false
    ).sort((a, b) => getStructureIndex(a) - getStructureIndex(b));
}

function collectParagraphContent(startIndex: number): string[] {
    return getParagraphRowsAfter(startIndex)
        .map(row => row.innerHTML || '')
        .filter(content => !isEmptyContent(content));
}

function triggerInputEvent(element: HTMLElement): void {
    if(!element) return;
    
    const event = new Event('input', {bubbles: true});
    element.dispatchEvent(event);
}

function updateElementContent(element: HTMLElement, content: string): void {
    if(!element) return;
    
    element.innerHTML = sanitizeHTML(content);
    triggerInputEvent(element);
}

function distributeLinesToRows(rows: HTMLElement[], lines: string[]): void {
    if(!rows.length || !lines.length) return;
    
    for(let i = 0; i < rows.length && i < lines.length; i++) {
        const row = rows[i];
        
        // Last row and still have lines left?
        if(i === rows.length - 1 && lines.length > i + 1) {
            // Concatenate all remaining lines with spaces
            const remaining = lines.slice(i).join(' ');
            updateElementContent(row, remaining);
        } else {
            updateElementContent(row, lines[i]);
        }
    }
}

function processVariableSpanSplit(
    node: Node,
    currentOffset: number,
    cursorOffset: number,
    beforeContainer: HTMLElement,
    afterContainer: HTMLElement
): {newOffset: number; splitDone: boolean} {
    return cloneNodeToContainer(node, currentOffset, cursorOffset, 1, beforeContainer, afterContainer);
}

function processTextNodeSplit(
    node: Node,
    currentOffset: number,
    cursorOffset: number,
    beforeContainer: HTMLElement,
    afterContainer: HTMLElement
): {newOffset: number; splitDone: boolean} {
    const textLength = node.textContent?.length || 0;
    
    // Entire text node is before cursor
    if(currentOffset + textLength <= cursorOffset) {
        beforeContainer.appendChild(node.cloneNode(true));
        return {newOffset: currentOffset + textLength, splitDone: false};
    }
    
    // Entire text node is after cursor
    if(currentOffset >= cursorOffset) {
        afterContainer.appendChild(node.cloneNode(true));
        return {newOffset: currentOffset, splitDone: true};
    }
    
    // Cursor is within this text node - split it
    const splitPoint = cursorOffset - currentOffset;
    const textContent = node.textContent || '';
    const beforeText = textContent.substring(0, splitPoint);
    const afterText = textContent.substring(splitPoint);
    
    if(beforeText) {
        beforeContainer.appendChild(document.createTextNode(beforeText));
    }
    if(afterText) {
        afterContainer.appendChild(document.createTextNode(afterText));
    }
    
    return {newOffset: currentOffset + textLength, splitDone: true};
}

export function splitContentAtCursor(element: HTMLElement, cursorOffset: number): {contentBefore: string; contentAfter: string} {
    if(!element) {
        return {contentBefore: '', contentAfter: ''};
    }
    
    const beforeContainer = document.createElement('div');
    const afterContainer = document.createElement('div');
    
    let currentOffset = 0;
    let splitDone = false;
    
    for(let i = 0; i < element.childNodes.length; i++) {
        const node = element.childNodes[i];
        
        if(splitDone) {
            afterContainer.appendChild(node.cloneNode(true));
            continue;
        }
        
        if(isVariableSpan(node)) {
            const result = processVariableSpanSplit(
                node,
                currentOffset,
                cursorOffset,
                beforeContainer,
                afterContainer
            );
            currentOffset = result.newOffset;
            splitDone = result.splitDone;
        } else if(node.nodeType === Node.TEXT_NODE) {
            const result = processTextNodeSplit(
                node,
                currentOffset,
                cursorOffset,
                beforeContainer,
                afterContainer
            );
            currentOffset = result.newOffset;
            splitDone = result.splitDone;
        } else {
            // Other node types - handle gracefully
            const result = cloneNodeToContainer(node, currentOffset, cursorOffset, 1, beforeContainer, afterContainer);
            currentOffset = result.newOffset;
            splitDone = result.splitDone;
        }
    }
    
    return {
        contentBefore: beforeContainer.innerHTML,
        contentAfter: afterContainer.innerHTML
    };
}

function getContentAfterCursor(element: HTMLElement, cursorOffset: number): string {
    const {contentAfter} = splitContentAtCursor(element, cursorOffset);
    return isEmptyContent(contentAfter) ? '' : contentAfter;
}

export function handleMultiLinePaste(
    currentElement: HTMLElement,
    pastedText: string,
    onChange?: (content: string) => void
): void {
    if(!currentElement || !pastedText) return;
    
    const pastedLines = pastedText.split('\n');
    if(pastedLines.length === 0) return;
    
    const startOffset = getCursorOffsetInElement(currentElement);
    const endOffset = getSelectionEndOffsetInElement(currentElement);
    const {contentBefore} = splitContentAtCursor(currentElement, startOffset);
    const currentIndex = getStructureIndex(currentElement);
    
    // Collect content to be pushed down (use endOffset to skip over any selected text)
    const contentAfter = getContentAfterCursor(currentElement, endOffset);
    const pushedContent: string[] = contentAfter ? [contentAfter] : [];
    // Only push existing paragraph content down when pasting from a paragraph row,
    // not when pasting from an inline param (inline params are simply overwritten)
    if(isParagraphRow(currentElement)) {
        const existingContent = collectParagraphContent(currentIndex);
        pushedContent.push(...existingContent);
    }
    
    // Update current row with: contentBefore + first pasted line
    const newContent = sanitizeHTML(contentBefore + pastedLines[0]);
    currentElement.innerHTML = newContent;
    
    if(onChange) {
        onChange(newContent);
    }
    
    // Prepare all lines to distribute: remaining pasted lines + pushed content
    const remainingPastedLines = pastedLines.slice(1);
    const allLinesToDistribute = [...remainingPastedLines, ...pushedContent];
    
    if(allLinesToDistribute.length === 0) return;
    
    const editableElementsAfter = getEditableElementsAfter(currentElement);
    const paragraphRowsAfter = editableElementsAfter.filter(el => isParagraphRow(el));
    distributeLinesToRows(paragraphRowsAfter, allLinesToDistribute);
}

function findNextParagraphRow(currentIndex: number): HTMLElement | null {
    const rows = findParagraphRows(currentIndex, (idx, ref) => idx > ref, true);
    return rows[0] || null;
}

function findPreviousParagraphRow(currentIndex: number): HTMLElement | null {
    const rows = findParagraphRows(currentIndex, (idx, ref) => idx < ref, false);
    return rows.sort((a, b) => getStructureIndex(b) - getStructureIndex(a))[0] || null;
}

function combineContentWithSpacing(first: string, second: string): string {
    if(!first) return second;
    if(!second) return first;
    
    const needsSpace = !endsWithWhitespace(first) && !startsWithWhitespace(second);
    return first + (needsSpace ? ' ' : '') + second;
}

export function splitTextToNextRow(
    currentElement: HTMLElement,
    onChange?: (content: string) => void
): boolean {
    if(!currentElement) return false;
    
    const selection = window.getSelection();
    if(!selection?.rangeCount) return false;
    
    const range = selection.getRangeAt(0);
    if(!currentElement.contains(range.startContainer)) return false;
    
    const cursorOffset = getCursorOffsetInElement(currentElement);
    const {contentBefore, contentAfter} = splitContentAtCursor(currentElement, cursorOffset);
    
    // If no content after cursor, don't proceed
    if(isEmptyContent(contentAfter)) {
        return false;
    }
    
    const newContent = sanitizeHTML(contentBefore);
    currentElement.innerHTML = newContent;
    
    if(onChange) {
        onChange(newContent);
    }
    
    // Find next paragraph row
    const currentIndex = getStructureIndex(currentElement);
    const nextParagraphRow = findNextParagraphRow(currentIndex);
    
    if(!nextParagraphRow) {
        return false;
    }
    
    // Prepare content for next row
    const existingContent = nextParagraphRow.innerHTML || '';
    const finalContent = isEmptyContent(existingContent)
        ? contentAfter
        : combineContentWithSpacing(contentAfter, existingContent);
    
    updateRowAndFocus(nextParagraphRow, finalContent, 0);
    return true;
}

export function mergeWithPreviousRow(currentElement: HTMLElement,onChange?: (content: string) => void): boolean {
    if(!currentElement) return false;
    
    const currentContent = currentElement.innerHTML || '';
    const currentIndex = getStructureIndex(currentElement);
    const previousParagraphRow = findPreviousParagraphRow(currentIndex);
    
    if(!previousParagraphRow) {
        return false;
    }
    
    const previousContent = previousParagraphRow.innerHTML || '';
    
    // Combine contents with proper spacing
    const finalContent = isEmptyContent(previousContent)
        ? currentContent
        : isEmptyContent(currentContent)
            ? previousContent
            : combineContentWithSpacing(previousContent, currentContent);
    
    const mergePoint = isEmptyContent(previousContent) 
        ? 0 
        : getTextContent(previousContent).length;
    
    // Update previous row with combined content and focus at merge point
    updateRowAndFocus(previousParagraphRow, finalContent, mergePoint);
    
    // Clear current row
    if(onChange) {
        onChange(EMPTY_PARAGRAPH);
    }
    return true;
}
